import { useEffect, useState } from "react";
import type { CatalogProduct } from "@/types";
import { UNSET_DASH } from "@/lib/format";
import { semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";
import { DetailPanel } from "@/components/DetailPanel";
import { ReadOnlySave } from "@/components/ReadOnlySave";

const LABEL: React.CSSProperties = {
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
  fontWeight: typography.fontWeight.medium, marginBottom: spacing[4],
};

const INPUT: React.CSSProperties = {
  width: "100%", padding: `${spacing[6]}px ${spacing[10]}px`, height: heights.input,
  fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.surface, outline: "none",
  boxSizing: "border-box",
};

function Field({
  label, testid, value, onChange, type = "text", readOnly = false,
}: {
  label: string;
  testid: string;
  value: string | number;
  onChange?: (v: string) => void;
  type?: "text" | "number";
  readOnly?: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <span style={LABEL}>{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        readOnly={readOnly}
        onChange={(e) => onChange?.(e.target.value)}
        style={{
          ...INPUT,
          fontVariantNumeric: type === "number" ? "tabular-nums" : "normal",
          textAlign: type === "number" ? "right" : "left",
          ...(readOnly ? { backgroundColor: semanticColors.panelBg, color: semanticColors.textSecondary } : null),
        }}
      />
    </div>
  );
}

/** Weight and volume as this panel shows them: the dash for both undefined
 *  AND zero, matching CatalogTable exactly. Zero is the master's "unmeasured"
 *  value -- it has no null state for these -- so 0.00 in an input box beside a
 *  table cell reading "—" for the same product would contradict itself on one
 *  screen. See CatalogTable.measurement for the full reasoning. */
function measurementText(value: number | undefined, digits: number): string {
  if (value === undefined || value === null || value === 0) return UNSET_DASH;
  return value.toFixed(digits);
}

// ⚠️ THIS PANEL READS THE REAL PRODUCT MASTER (slice 1c). It lost the three
// fields the mock catalog invented -- spec, listPrice, stock -- because
// anixify.product has no such columns; see CatalogTable's header comment.
//
// Every field here is display. Save has been read-only across all six
// reference-data panels since before this slice (ReadOnlySave), and there is
// no write path to the master from the SPA.
export function ProductDetailPanel({
  product,
  onClose,
}: {
  product: CatalogProduct | null;
  onClose: () => void;
}) {
  const [form, setForm] = useState<CatalogProduct | null>(product);

  useEffect(() => {
    setForm(product);
  }, [product]);

  if (!product || !form) return null;

  const setField = <K extends keyof CatalogProduct>(k: K, v: CatalogProduct[K]) => {
    setForm((cur) => (cur ? { ...cur, [k]: v } : cur));
  };

  return (
    <DetailPanel
      testIdPrefix="product"
      title={form.description || form.partNo}
      subtitle={form.partNo}
      onClose={onClose}
      footer={
        <>
          <button
            data-testid="product-panel-cancel"
            className="btn-secondary"
            onClick={onClose}
            style={{
              height: heights.control, padding: `0 ${spacing[14]}px`,
              backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
              border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
              fontSize: typography.fontSize.base, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <ReadOnlySave testId="product-panel-save" />
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14] }}>
        {/* The identity the rest of the app keys on -- selection, line
            matching and the save wire all use this, never the part number. */}
        <Field label="Catalog ID"    testid="pf-id"           value={form.id}                       readOnly />
        <Field label="Part No."      testid="pf-partNo"       value={form.partNo}                   onChange={(v) => setField("partNo", v)} />
        {/* ⚠️ READ-ONLY BECAUSE THEY ARE JOIN KEYS, not because the panel is.
            brand and category are compared with `===` against backend taxonomy
            names, and this build's unions are closed over them. An editable box
            would let a free-typed string into the field brand-lock evaluation
            matches on -- a cast by another route. Null is a real state here
            (unclassified, or a name this build cannot resolve), and it says so
            rather than rendering an empty box that reads as a loading glitch. */}
        <Field label="Brand"         testid="pf-brand"        value={form.brand ?? "Unclassified"}     readOnly />
        <Field label="Category"      testid="pf-category"     value={form.category ?? "Unclassified"}  readOnly />
        <Field label="Manufacturer"  testid="pf-manufacturer" value={form.manufacturer ?? ""}       onChange={(v) => setField("manufacturer", v)} />
        {/* DISPLAY ONLY, AND IT STOPS HERE -- not a Material field, not on any
            save wire, never matched on. See CatalogProduct.supplierRef. */}
        <Field label="Supplier Ref"  testid="pf-supplierRef"  value={form.supplierRef ?? ""}        onChange={(v) => setField("supplierRef", v)} />
        <Field label="HSN Code"      testid="pf-hsnCode"      value={form.hsnCode ?? ""}            onChange={(v) => setField("hsnCode", v)} />
        <Field label="UoM"           testid="pf-uom"          value={form.uom ?? ""}                onChange={(v) => setField("uom", v)} />
        {/* Text, not number inputs: they render the dash for the unmeasured
            sentinel, and a number input cannot hold one. */}
        <Field label="Weight kg"     testid="pf-weight"       value={measurementText(form.weight, 2)} readOnly />
        <Field label="Volume CBM"    testid="pf-volume"       value={measurementText(form.volume, 4)} readOnly />
        <Field label="Default Origin" testid="pf-defaultOrigin" value={form.defaultOrigin ?? ""}    onChange={(v) => setField("defaultOrigin", v)} />
        {/* HOW this row got here, so a browser can tell ERP master data from a
            one-off Sales typed. READ-ONLY, and not on any setField path: the
            backend stamps it from the code path that did the write and drops
            it from the input allowlist, so an editable box would be a control
            that cannot possibly do anything.

            ⚠️ An unrecognised value passes through UNMAPPED rather than
            falling back to either label. The column is a Selection today, but
            a third value added server-side must read as itself here, not be
            silently relabelled as one of the two this build happens to know.

            There is no author beside it on purpose: created_by_uid answers WHO
            and is deliberately not serialized -- a res.users id is not a fact
            the API owes a browser. Do not add one. */}
        <Field label="Source"        testid="pf-source"
               value={form.source === "erp" ? "ERP"
                    : form.source === "manual" ? "Sales one-off"
                    : form.source}                                            readOnly />
      </div>
    </DetailPanel>
  );
}
